import { useEffect, useMemo, useRef, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Sparkles, Wand2, Search, Loader2, Copy, Check, Plus, X, FileText, Blocks,
  ExternalLink, ImageIcon, RefreshCw, History, Trash2, Save,
} from "lucide-react";
import { toast } from "sonner";

/**
 * AI Email Copywriter — Simple text-only mode.
 *
 * User writes the brief (full or per-module). The tool generates the email
 * COPY in English using the learned Mailchimp style guide.
 * Per module, a Shutterstock search button opens a results grid; clicking
 * any photo opens shutterstock.com in a new tab so the user can download
 * with their own account and upload to Mailchimp manually.
 *
 * No HTML rendering, no Mailchimp push, no MJML — just copy + photo search.
 */
export default function EmailDesigner() {
  const [mode, setMode] = useState("brief"); // brief | modules

  // Brief mode
  const [brief, setBrief] = useState("");
  const [numModules, setNumModules] = useState(3);

  // Modules mode — list of {brief, heading_hint}
  const [modulesInput, setModulesInput] = useState([
    { brief: "", heading_hint: "" },
    { brief: "", heading_hint: "" },
    { brief: "", heading_hint: "" },
  ]);

  // Shared options
  const [audienceId, setAudienceId] = useState("");
  const [audiences, setAudiences] = useState([]);
  const [emailType, setEmailType] = useState("promotional");
  const [ctaLabel, setCtaLabel] = useState("Plan Your Trip");
  const [ctaUrl, setCtaUrl] = useState("https://espiritutravel.com/contact");

  const [styleGuideExists, setStyleGuideExists] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [copy, setCopy] = useState(null);

  // Photo search modal
  const [photoModal, setPhotoModal] = useState({ open: false, moduleIndex: null, query: "" });
  const [copiedAll, setCopiedAll] = useState(false);

  // Saved drafts (history)
  const [draftId, setDraftId] = useState(null);
  const [savedAt, setSavedAt] = useState(null);
  const [autoSaving, setAutoSaving] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [drafts, setDrafts] = useState([]);
  const [draftsLoading, setDraftsLoading] = useState(false);
  const autoSaveTimer = useRef(null);

  useEffect(() => {
    Promise.all([
      api.get("/email-designer/audiences").then(r => setAudiences(r.data || [])).catch(() => {}),
      api.get("/mailchimp/style-guide").then(r => setStyleGuideExists(!!r.data?.exists)).catch(() => setStyleGuideExists(false)),
    ]);
  }, []);

  const audience = useMemo(
    () => audiences.find(a => a.id === audienceId),
    [audiences, audienceId]
  );

  const handleGenerate = async () => {
    if (mode === "brief" && !brief.trim()) {
      toast.error("Escribe un briefing primero");
      return;
    }
    if (mode === "modules" && !modulesInput.some(m => m.brief.trim())) {
      toast.error("Al menos un módulo necesita briefing");
      return;
    }

    setGenerating(true);
    setCopy(null);
    try {
      const payload = {
        mode,
        audience_name: audience?.name || null,
        email_type: emailType,
        cta_label: ctaLabel,
        cta_url: ctaUrl,
      };
      if (mode === "brief") {
        payload.brief = brief;
        payload.num_modules = numModules;
      } else {
        payload.brief = brief; // overall hook / titular base (optional)
        payload.modules = modulesInput
          .filter(m => m.brief.trim() || m.heading_hint?.trim())
          .map(m => ({
            brief: m.brief.trim(),
            heading_hint: (m.heading_hint || "").trim() || null,
          }));
      }
      const r = await api.post("/email-copywriter/generate", payload);
      setCopy(r.data.copy);
      setDraftId(r.data.id || null);
      setSavedAt(new Date().toISOString());
      toast.success("Textos generados y guardados");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error generando los textos");
    } finally {
      setGenerating(false);
    }
  };

  // Debounced auto-save on copy/cta_url changes
  useEffect(() => {
    if (!draftId || !copy) return;
    if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current);
    autoSaveTimer.current = setTimeout(() => {
      setAutoSaving(true);
      api.put(`/email-copywriter/drafts/${draftId}`, { copy, cta_url: ctaUrl })
        .then(r => setSavedAt(r.data.updated_at || new Date().toISOString()))
        .catch(() => { /* silent */ })
        .finally(() => setAutoSaving(false));
    }, 1500);
    return () => { if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [copy, ctaUrl, draftId]);

  const openHistory = async () => {
    setHistoryOpen(true);
    setDraftsLoading(true);
    try {
      const r = await api.get("/email-copywriter/drafts", { params: { limit: 100 } });
      setDrafts(r.data || []);
    } catch (e) {
      toast.error("No se pudo cargar el historial");
    } finally {
      setDraftsLoading(false);
    }
  };

  const loadDraft = async (id) => {
    try {
      const r = await api.get(`/email-copywriter/drafts/${id}`);
      const d = r.data;
      setCopy(d.copy);
      setDraftId(d.id);
      setSavedAt(d.updated_at || d.created_at);
      // Restore form state
      if (d.mode) setMode(d.mode);
      if (d.brief !== undefined) setBrief(d.brief || "");
      if (d.num_modules) setNumModules(d.num_modules);
      if (Array.isArray(d.modules_input) && d.modules_input.length > 0) {
        setModulesInput(d.modules_input);
      }
      if (d.email_type) setEmailType(d.email_type);
      if (d.cta_label) setCtaLabel(d.cta_label);
      if (d.cta_url) setCtaUrl(d.cta_url);
      setHistoryOpen(false);
      toast.success("Borrador cargado");
    } catch (e) {
      toast.error("No se pudo cargar el borrador");
    }
  };

  const deleteDraft = async (id) => {
    if (!window.confirm("¿Borrar este borrador? Esta acción no se puede deshacer.")) return;
    try {
      await api.delete(`/email-copywriter/drafts/${id}`);
      setDrafts(prev => prev.filter(d => d.id !== id));
      if (draftId === id) { setDraftId(null); setSavedAt(null); }
      toast.success("Borrador eliminado");
    } catch {
      toast.error("No se pudo eliminar");
    }
  };

  const newDraft = () => {
    setCopy(null);
    setDraftId(null);
    setSavedAt(null);
  };

  // Inline edit a top-level copy field
  const updateField = (key, value) => {
    setCopy(prev => ({ ...prev, [key]: value }));
  };
  const updateModule = (idx, key, value) => {
    setCopy(prev => {
      const next = [...(prev?.modules || [])];
      next[idx] = { ...next[idx], [key]: value };
      return { ...prev, modules: next };
    });
  };

  // Modules input helpers (modules mode, BEFORE generation)
  const addModuleInput = () => {
    if (modulesInput.length >= 8) {
      toast.error("Máximo 8 módulos");
      return;
    }
    setModulesInput([...modulesInput, { brief: "", heading_hint: "" }]);
  };
  const removeModuleInput = (i) => {
    if (modulesInput.length <= 1) return;
    setModulesInput(modulesInput.filter((_, idx) => idx !== i));
  };
  const updateModuleInput = (i, key, value) => {
    const next = [...modulesInput];
    next[i] = { ...next[i], [key]: value };
    setModulesInput(next);
  };

  const openPhotoSearch = (moduleIndex, defaultQuery) => {
    setPhotoModal({ open: true, moduleIndex, query: defaultQuery || "" });
  };

  // Called from inside the photo-search dialog when user picks one.
  // Saves it as the module's selected photo so the user remembers which
  // image they chose (it doesn't download it — the user does that manually).
  const handlePhotoPicked = (photo) => {
    const idx = photoModal.moduleIndex;
    if (idx == null) return;
    updateModule(idx, "selected_photo", {
      id: photo.id,
      preview_url: photo.preview_url,
      description: photo.description,
      shutterstock_url: "https://www.shutterstock.com/image-photo/-" + photo.id,
    });
    setPhotoModal({ ...photoModal, open: false });
    toast.success(`Foto fijada en Módulo ${idx + 1}. Descárgala en la pestaña que se abrió.`);
  };

  // Build plain-text export for clipboard
  const buildPlainExport = () => {
    if (!copy) return "";
    const lines = [];
    lines.push(`Subject: ${copy.subject_line || ""}`);
    if (copy.subject_variants?.length) {
      lines.push("");
      lines.push("Subject variants:");
      copy.subject_variants.forEach((v, i) => lines.push(`  ${i + 1}. ${v}`));
    }
    lines.push("");
    lines.push(`Preheader: ${copy.preheader || ""}`);
    lines.push("");
    lines.push(`Header text:\n${copy.header_text || ""}`);
    if (copy.intro_paragraph) {
      lines.push("");
      lines.push(`Intro:\n${copy.intro_paragraph}`);
    }
    (copy.modules || []).forEach((m, i) => {
      lines.push("");
      lines.push(`──── Module ${i + 1} ────`);
      lines.push(`Heading: ${m.heading || ""}`);
      lines.push("");
      lines.push(m.body || "");
      if (m.suggested_search_query) {
        lines.push("");
        lines.push(`[photo search: ${m.suggested_search_query}]`);
      }
    });
    lines.push("");
    lines.push(`CTA: ${copy.cta_label || ""}  →  ${ctaUrl}`);
    if (copy.ps) {
      lines.push("");
      lines.push(`P.S. ${copy.ps}`);
    }
    return lines.join("\n");
  };

  const copyAll = async () => {
    try {
      await navigator.clipboard.writeText(buildPlainExport());
      setCopiedAll(true);
      toast.success("Copiado al portapapeles");
      setTimeout(() => setCopiedAll(false), 2000);
    } catch {
      toast.error("No se pudo copiar");
    }
  };

  return (
    <div className="space-y-6" data-testid="email-designer">
      {/* Header */}
      <Card className="edit-card p-6 rounded-sm shadow-none">
        <div className="flex items-start gap-3">
          <Wand2 className="w-6 h-6 text-[#D95D39] mt-0.5" />
          <div className="flex-1">
            <h3 className="font-display text-2xl text-[#0A1128]">AI Email Copywriter</h3>
            <p className="text-xs text-[#8A8F98] mt-1 max-w-2xl">
              Genera textos en inglés (asunto, preheader, cabecera y módulos) a partir de un briefing
              completo o por módulo. La IA aprende el tono de tus mejores campañas pasadas.
              Luego busca fotos en Shutterstock y monta el email tú mismo en Mailchimp.
            </p>
          </div>
          {styleGuideExists === false && (
            <Badge variant="outline" className="rounded-sm border-amber-500 text-amber-700">
              Style guide pendiente
            </Badge>
          )}
        </div>

        {/* Mode switcher + history */}
        <div className="mt-5 flex flex-wrap items-center gap-2" data-testid="mode-switcher">
          <Button
            data-testid="mode-brief-btn"
            variant={mode === "brief" ? "default" : "outline"}
            onClick={() => setMode("brief")}
            className={`rounded-sm ${mode === "brief" ? "bg-[#0A1128] text-white" : ""}`}
          >
            <FileText className="w-4 h-4 mr-2" /> Briefing completo
          </Button>
          <Button
            data-testid="mode-modules-btn"
            variant={mode === "modules" ? "default" : "outline"}
            onClick={() => setMode("modules")}
            className={`rounded-sm ${mode === "modules" ? "bg-[#0A1128] text-white" : ""}`}
          >
            <Blocks className="w-4 h-4 mr-2" /> Briefing por módulo
          </Button>
          <div className="flex-1" />
          {copy && (
            <Button
              data-testid="new-draft-btn"
              variant="ghost"
              size="sm"
              onClick={newDraft}
              className="rounded-sm text-[#8A8F98] hover:text-[#0A1128]"
            >
              <Plus className="w-3.5 h-3.5 mr-1.5" /> Nuevo
            </Button>
          )}
          <Button
            data-testid="history-btn"
            variant="outline"
            size="sm"
            onClick={openHistory}
            className="rounded-sm border-[#E2DCD0]"
          >
            <History className="w-3.5 h-3.5 mr-1.5" /> Mis generaciones
          </Button>
        </div>

        {/* Saved indicator */}
        {draftId && (
          <div className="mt-2 text-[10px] text-[#8A8F98] flex items-center gap-1.5" data-testid="saved-indicator">
            {autoSaving ? (
              <><Loader2 className="w-3 h-3 animate-spin" /> Guardando…</>
            ) : (
              <><Save className="w-3 h-3 text-green-600" /> Guardado{savedAt && ` · ${new Date(savedAt).toLocaleTimeString("es-ES", { hour: "2-digit", minute: "2-digit" })}`}</>
            )}
          </div>
        )}
      </Card>

      <div className="grid lg:grid-cols-5 gap-6">
        {/* LEFT — inputs */}
        <div className="lg:col-span-2 space-y-4">
          <Card className="edit-card p-6 rounded-sm shadow-none space-y-4">
            {mode === "brief" ? (
              <>
                <div>
                  <Label className="text-xs uppercase text-[#8A8F98] tracking-wider">Briefing completo</Label>
                  <Textarea
                    data-testid="brief-input"
                    placeholder="Ej: Promocionar viajes a Andalucía en primavera para clientes pasados. Foco en Sevilla y Córdoba en abril, festival de los patios. Tono nostálgico, evocador, no promocional."
                    value={brief}
                    onChange={e => setBrief(e.target.value)}
                    rows={7}
                    className="rounded-sm mt-1"
                  />
                  <p className="text-[10px] text-[#8A8F98] mt-1">Puedes escribir en español. La salida será en inglés.</p>
                </div>
                <div>
                  <Label className="text-xs uppercase text-[#8A8F98] tracking-wider">Número de módulos</Label>
                  <Select value={String(numModules)} onValueChange={v => setNumModules(parseInt(v, 10))}>
                    <SelectTrigger data-testid="num-modules-select" className="rounded-sm mt-1 w-32">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {[1, 2, 3, 4, 5, 6, 7, 8].map(n => (
                        <SelectItem key={n} value={String(n)}>{n}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </>
            ) : (
              <>
                <div>
                  <Label className="text-xs uppercase text-[#8A8F98] tracking-wider">Titular base (opcional)</Label>
                  <Input
                    data-testid="modules-overall-brief"
                    placeholder="Ej: Estás a tiempo de reservar tu viaje de verano y otoño"
                    value={brief}
                    onChange={e => setBrief(e.target.value)}
                    className="rounded-sm mt-1 text-sm"
                  />
                  <p className="text-[10px] text-[#8A8F98] mt-1">El gancho global que define el asunto y la cabecera del email.</p>
                </div>
                <div className="flex items-center justify-between">
                  <Label className="text-xs uppercase text-[#8A8F98] tracking-wider">
                    Briefing por módulo ({modulesInput.length})
                  </Label>
                  <Button size="sm" variant="outline" className="rounded-sm h-7"
                    onClick={addModuleInput} data-testid="add-module-input">
                    <Plus className="w-3.5 h-3.5 mr-1" /> Añadir módulo
                  </Button>
                </div>
                {modulesInput.map((m, i) => (
                  <div key={i} className="border border-[#E2DCD0] rounded-sm p-3 bg-[#F9F8F6]/30 space-y-2 relative">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-medium text-[#0A1128]">Módulo {i + 1}</span>
                      {modulesInput.length > 1 && (
                        <button
                          onClick={() => removeModuleInput(i)}
                          className="text-[#8A8F98] hover:text-red-600 p-0.5"
                          data-testid={`remove-module-${i}`}
                          aria-label={`Eliminar módulo ${i + 1}`}
                        >
                          <X className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                    <Textarea
                      data-testid={`module-brief-${i}`}
                      placeholder={`Ej: La experiencia del festival de los patios — qué se siente, por qué es único.`}
                      value={m.brief}
                      onChange={e => updateModuleInput(i, "brief", e.target.value)}
                      rows={3}
                      className="rounded-sm text-sm"
                    />
                    <Input
                      data-testid={`module-heading-hint-${i}`}
                      placeholder="(Opcional) sugerencia de titular en inglés"
                      value={m.heading_hint}
                      onChange={e => updateModuleInput(i, "heading_hint", e.target.value)}
                      className="rounded-sm text-xs h-8"
                    />
                  </div>
                ))}
              </>
            )}

            {/* Common options */}
            <div className="border-t border-[#E2DCD0] pt-4 space-y-3">
              <div>
                <Label className="text-xs uppercase text-[#8A8F98]">Audiencia (opcional)</Label>
                <Select value={audienceId} onValueChange={setAudienceId}>
                  <SelectTrigger data-testid="audience-select" className="rounded-sm mt-1">
                    <SelectValue placeholder="Sin audiencia específica" />
                  </SelectTrigger>
                  <SelectContent>
                    {audiences.map(a => (
                      <SelectItem key={a.id} value={a.id}>
                        {a.name} ({a.member_count})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs uppercase text-[#8A8F98]">Tipo</Label>
                  <Select value={emailType} onValueChange={setEmailType}>
                    <SelectTrigger className="rounded-sm mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="promotional">Promocional</SelectItem>
                      <SelectItem value="newsletter">Newsletter</SelectItem>
                      <SelectItem value="announcement">Anuncio</SelectItem>
                      <SelectItem value="nurture">Nurture</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs uppercase text-[#8A8F98]">CTA label</Label>
                  <Input value={ctaLabel} onChange={e => setCtaLabel(e.target.value)} className="rounded-sm mt-1" />
                </div>
              </div>
              <div>
                <Label className="text-xs uppercase text-[#8A8F98]">CTA URL</Label>
                <Input value={ctaUrl} onChange={e => setCtaUrl(e.target.value)} className="rounded-sm mt-1" />
              </div>
            </div>

            <Button
              data-testid="generate-btn"
              className="w-full bg-[#D95D39] hover:bg-[#c54e2e] text-white rounded-sm"
              disabled={generating}
              onClick={handleGenerate}
            >
              {generating
                ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Generando textos…</>
                : <><Sparkles className="w-4 h-4 mr-2" /> Generar textos</>
              }
            </Button>
          </Card>
        </div>

        {/* RIGHT — output */}
        <div className="lg:col-span-3 space-y-4">
          {!copy && !generating && (
            <Card className="edit-card rounded-sm shadow-none p-16 text-center" data-testid="output-empty">
              <Wand2 className="w-12 h-12 mx-auto text-[#E2DCD0] mb-3" />
              <h4 className="font-display text-xl text-[#0A1128]">Tus textos aparecerán aquí</h4>
              <p className="text-sm text-[#8A8F98] mt-1">
                Escribe un briefing y pulsa <strong>Generar textos</strong>
              </p>
            </Card>
          )}

          {generating && (
            <Card className="edit-card rounded-sm shadow-none p-12 text-center">
              <Loader2 className="w-10 h-10 mx-auto text-[#D95D39] mb-3 animate-spin" />
              <p className="text-sm text-[#0A1128]">Generando textos con tu voz de marca…</p>
            </Card>
          )}

          {copy && (
            <>
              {/* Toolbar */}
              <div className="flex items-center gap-2 sticky top-0 z-10 bg-[#F2EFE8]/95 backdrop-blur-sm py-2 -my-2 px-1">
                <Badge variant="outline" className="rounded-sm">
                  {(copy.modules || []).length} módulos
                </Badge>
                <div className="flex-1" />
                <Button
                  size="sm"
                  variant="outline"
                  onClick={handleGenerate}
                  disabled={generating}
                  className="rounded-sm h-8"
                  data-testid="regenerate-all-btn"
                >
                  <RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Regenerar todo
                </Button>
                <Button
                  size="sm"
                  onClick={copyAll}
                  className="rounded-sm h-8 bg-[#0A1128] text-white hover:bg-[#1a2240]"
                  data-testid="copy-all-btn"
                >
                  {copiedAll
                    ? <><Check className="w-3.5 h-3.5 mr-1.5" /> Copiado</>
                    : <><Copy className="w-3.5 h-3.5 mr-1.5" /> Copiar todo</>}
                </Button>
              </div>

              {/* Subject */}
              <Card className="edit-card p-5 rounded-sm shadow-none" data-testid="card-subject">
                <Label className="text-[10px] uppercase text-[#8A8F98] tracking-widest">Subject</Label>
                <div className="flex items-center gap-1 mt-1">
                  <Input
                    data-testid="field-subject"
                    value={copy.subject_line || ""}
                    onChange={e => updateField("subject_line", e.target.value)}
                    className="rounded-sm font-medium text-base"
                  />
                  <RefineFieldButton
                    field="subject_line"
                    currentValue={copy.subject_line}
                    context={copy}
                    onAccept={(v) => updateField("subject_line", v)}
                    testId="refine-subject"
                  />
                  <CopyIconButton value={copy.subject_line} />
                </div>
                <div className="text-[10px] text-[#8A8F98] mt-1">
                  {(copy.subject_line || "").length} caracteres
                </div>

                {copy.subject_variants?.length > 0 && (
                  <div className="mt-3">
                    <Label className="text-[10px] uppercase text-[#8A8F98] tracking-widest">Alternativas (click para usar)</Label>
                    <div className="flex flex-wrap gap-1.5 mt-1.5">
                      {copy.subject_variants.map((s, i) => (
                        <button
                          key={i}
                          data-testid={`subject-variant-${i}`}
                          className="text-xs px-2 py-1 border border-[#E2DCD0] rounded-sm hover:border-[#D95D39] hover:text-[#D95D39] transition-colors"
                          onClick={() => updateField("subject_line", s)}
                        >{s}</button>
                      ))}
                    </div>
                  </div>
                )}
              </Card>

              {/* Preheader */}
              <Card className="edit-card p-5 rounded-sm shadow-none" data-testid="card-preheader">
                <Label className="text-[10px] uppercase text-[#8A8F98] tracking-widest">Preheader</Label>
                <div className="flex items-center gap-1 mt-1">
                  <Input
                    data-testid="field-preheader"
                    value={copy.preheader || ""}
                    onChange={e => updateField("preheader", e.target.value)}
                    className="rounded-sm"
                  />
                  <RefineFieldButton
                    field="preheader"
                    currentValue={copy.preheader}
                    context={copy}
                    onAccept={(v) => updateField("preheader", v)}
                    testId="refine-preheader"
                  />
                  <CopyIconButton value={copy.preheader} />
                </div>
                <div className="text-[10px] text-[#8A8F98] mt-1">
                  {(copy.preheader || "").length} caracteres
                </div>
              </Card>

              {/* Header text */}
              <Card className="edit-card p-5 rounded-sm shadow-none" data-testid="card-header">
                <Label className="text-[10px] uppercase text-[#8A8F98] tracking-widest">Main text · cabecera</Label>
                <div className="flex items-start gap-1 mt-1">
                  <Textarea
                    data-testid="field-header-text"
                    value={copy.header_text || ""}
                    onChange={e => updateField("header_text", e.target.value)}
                    rows={2}
                    className="rounded-sm font-display text-lg text-[#0A1128]"
                  />
                  <RefineFieldButton
                    field="header_text"
                    currentValue={copy.header_text}
                    context={copy}
                    onAccept={(v) => updateField("header_text", v)}
                    testId="refine-header"
                  />
                  <CopyIconButton value={copy.header_text} />
                </div>
              </Card>

              {/* Intro */}
              {copy.intro_paragraph !== undefined && (
                <Card className="edit-card p-5 rounded-sm shadow-none" data-testid="card-intro">
                  <Label className="text-[10px] uppercase text-[#8A8F98] tracking-widest">Intro (opcional)</Label>
                  <div className="flex items-start gap-1 mt-1">
                    <Textarea
                      data-testid="field-intro"
                      value={copy.intro_paragraph || ""}
                      onChange={e => updateField("intro_paragraph", e.target.value)}
                      rows={3}
                      placeholder="Sin intro"
                      className="rounded-sm"
                    />
                    <RefineFieldButton
                      field="intro_paragraph"
                      currentValue={copy.intro_paragraph}
                      context={copy}
                      onAccept={(v) => updateField("intro_paragraph", v)}
                      testId="refine-intro"
                    />
                    <CopyIconButton value={copy.intro_paragraph} />
                  </div>
                </Card>
              )}

              {/* Modules */}
              {(copy.modules || []).map((m, i) => (
                <Card key={i} className="edit-card p-5 rounded-sm shadow-none" data-testid={`card-module-${i}`}>
                  <div className="flex items-center justify-between mb-3">
                    <Label className="text-[10px] uppercase text-[#8A8F98] tracking-widest">
                      Módulo {i + 1}
                    </Label>
                  </div>

                  {/* PHOTO SLOT — prominent */}
                  <ModulePhotoSlot
                    photo={m.selected_photo}
                    onOpenSearch={() => openPhotoSearch(i, m.suggested_search_query)}
                    onClear={() => updateModule(i, "selected_photo", null)}
                    suggestedQuery={m.suggested_search_query}
                    testIdPrefix={`module-${i}`}
                  />

                  <div className="space-y-3 mt-4">
                    <div>
                      <Label className="text-[10px] uppercase text-[#8A8F98]">Heading</Label>
                      <div className="flex items-center gap-1 mt-1">
                        <Input
                          data-testid={`field-module-${i}-heading`}
                          value={m.heading || ""}
                          onChange={e => updateModule(i, "heading", e.target.value)}
                          className="rounded-sm font-display text-base"
                        />
                        <RefineFieldButton
                          field="module_heading"
                          currentValue={m.heading}
                          context={copy}
                          moduleIndex={i}
                          onAccept={(v) => updateModule(i, "heading", v)}
                          testId={`refine-module-${i}-heading`}
                        />
                        <CopyIconButton value={m.heading} />
                      </div>
                    </div>
                    <div>
                      <Label className="text-[10px] uppercase text-[#8A8F98]">Body</Label>
                      <div className="flex items-start gap-1 mt-1">
                        <Textarea
                          data-testid={`field-module-${i}-body`}
                          value={m.body || ""}
                          onChange={e => updateModule(i, "body", e.target.value)}
                          rows={6}
                          className="rounded-sm leading-relaxed"
                        />
                        <RefineFieldButton
                          field="module_body"
                          currentValue={m.body}
                          context={copy}
                          moduleIndex={i}
                          onAccept={(v) => updateModule(i, "body", v)}
                          testId={`refine-module-${i}-body`}
                        />
                        <CopyIconButton value={m.body} />
                      </div>
                      <div className="text-[10px] text-[#8A8F98] mt-1">
                        {(m.body || "").split(/\s+/).filter(Boolean).length} palabras
                      </div>
                    </div>
                  </div>
                </Card>
              ))}

              {/* CTA + PS */}
              <Card className="edit-card p-5 rounded-sm shadow-none">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-[10px] uppercase text-[#8A8F98] tracking-widest">CTA label</Label>
                    <Input
                      data-testid="field-cta-label"
                      value={copy.cta_label || ""}
                      onChange={e => updateField("cta_label", e.target.value)}
                      className="rounded-sm mt-1 font-medium"
                    />
                  </div>
                  <div>
                    <Label className="text-[10px] uppercase text-[#8A8F98] tracking-widest">CTA URL</Label>
                    <Input
                      value={ctaUrl}
                      onChange={e => setCtaUrl(e.target.value)}
                      className="rounded-sm mt-1 text-xs"
                    />
                  </div>
                </div>
                {(copy.ps !== undefined) && (
                  <div className="mt-3">
                    <Label className="text-[10px] uppercase text-[#8A8F98] tracking-widest">P.S. (opcional)</Label>
                    <Input
                      data-testid="field-ps"
                      value={copy.ps || ""}
                      onChange={e => updateField("ps", e.target.value)}
                      placeholder="Sin postdata"
                      className="rounded-sm mt-1"
                    />
                  </div>
                )}
              </Card>
            </>
          )}
        </div>
      </div>

      <ShutterstockSearchDialog
        key={`ssdlg-${photoModal.moduleIndex}-${photoModal.query}`}
        open={photoModal.open}
        onOpenChange={(o) => setPhotoModal({ ...photoModal, open: o })}
        defaultQuery={photoModal.query}
        moduleIndex={photoModal.moduleIndex}
        onPick={handlePhotoPicked}
      />

      <HistoryDialog
        open={historyOpen}
        onOpenChange={setHistoryOpen}
        drafts={drafts}
        loading={draftsLoading}
        currentId={draftId}
        onLoad={loadDraft}
        onDelete={deleteDraft}
        onRefresh={openHistory}
      />
    </div>
  );
}

// =============================================================================
// Module photo slot — prominent visual slot inside each module card.
// Shows the chosen Shutterstock preview (with watermark) or a CTA placeholder.
// =============================================================================

function ModulePhotoSlot({ photo, onOpenSearch, onClear, suggestedQuery, testIdPrefix }) {
  if (photo && photo.preview_url) {
    return (
      <div
        className="relative border-2 border-[#D95D39]/40 rounded-sm overflow-hidden group bg-[#F9F8F6]"
        data-testid={`${testIdPrefix}-photo-slot`}
      >
        <img
          src={photo.preview_url}
          alt={photo.description || "Foto elegida"}
          className="w-full h-44 object-cover"
        />
        <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/85 via-black/50 to-transparent px-3 py-2 flex items-center justify-between gap-2">
          <div className="min-w-0 text-white">
            <p className="text-[10px] uppercase tracking-wider opacity-70">Foto elegida</p>
            <p className="text-xs truncate">ID Shutterstock {photo.id}</p>
          </div>
          <div className="flex gap-1.5 shrink-0">
            <a
              href={photo.shutterstock_url || ("https://www.shutterstock.com/image-photo/-" + photo.id)}
              target="_blank"
              rel="noopener noreferrer"
              className="px-2 py-1 bg-white text-[#0A1128] text-[10px] font-medium rounded-sm hover:bg-[#F2EFE8] inline-flex items-center gap-1"
              data-testid={`${testIdPrefix}-photo-open-link`}
              title="Abrir en Shutterstock para descargar"
            >
              <ExternalLink className="w-3 h-3" /> Descargar
            </a>
            <button
              type="button"
              onClick={onOpenSearch}
              className="px-2 py-1 bg-[#D95D39] text-white text-[10px] font-medium rounded-sm hover:bg-[#c54e2e]"
              data-testid={`${testIdPrefix}-photo-change`}
              title="Cambiar foto"
            >
              Cambiar
            </button>
            <button
              type="button"
              onClick={onClear}
              className="p-1 bg-white/10 text-white rounded-sm hover:bg-white/20"
              data-testid={`${testIdPrefix}-photo-clear`}
              title="Quitar foto elegida"
            >
              <X className="w-3 h-3" />
            </button>
          </div>
        </div>
      </div>
    );
  }
  // Empty state — big visual CTA
  return (
    <button
      type="button"
      onClick={onOpenSearch}
      data-testid={`${testIdPrefix}-photo-slot-empty`}
      className="w-full h-32 border-2 border-dashed border-[#D95D39]/50 bg-[#D95D39]/5 rounded-sm flex flex-col items-center justify-center gap-1.5 hover:bg-[#D95D39]/10 hover:border-[#D95D39] transition-colors group"
    >
      <ImageIcon className="w-7 h-7 text-[#D95D39] group-hover:scale-110 transition-transform" />
      <p className="text-sm font-medium text-[#D95D39]">Buscar foto para este módulo</p>
      {suggestedQuery && (
        <p className="text-[10px] text-[#8A8F98]">Búsqueda sugerida: <code className="px-1 bg-white rounded">{suggestedQuery}</code></p>
      )}
    </button>
  );
}

// =============================================================================
// Copy-icon button — small clickable to copy a single field
// =============================================================================

function CopyIconButton({ value }) {
  const [copied, setCopied] = useState(false);
  const onClick = async () => {
    if (!value) return;
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch { /* noop */ }
  };
  return (
    <button
      type="button"
      onClick={onClick}
      className="shrink-0 p-2 text-[#8A8F98] hover:text-[#0A1128] transition-colors"
      title="Copiar"
      aria-label="Copiar"
    >
      {copied ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4" />}
    </button>
  );
}

// =============================================================================
// Refine-field button — opens a popover with an adjustment brief input,
// calls Claude to produce a refined version + 2 alternatives, lets the user
// accept (replaces the field) or discard.
// =============================================================================

function RefineFieldButton({ field, currentValue, context, moduleIndex, onAccept, testId }) {
  const [open, setOpen] = useState(false);
  const [adjustment, setAdjustment] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const run = async () => {
    if (!adjustment.trim()) return;
    setLoading(true);
    setResult(null);
    try {
      const r = await api.post("/email-copywriter/refine-field", {
        field,
        current_value: currentValue || "",
        adjustment: adjustment.trim(),
        context: context || {},
        module_index: moduleIndex ?? null,
      });
      setResult(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error refinando con IA");
    } finally {
      setLoading(false);
    }
  };

  const accept = (text) => {
    if (text) onAccept(text);
    setOpen(false);
    setAdjustment("");
    setResult(null);
    toast.success("Aplicado");
  };

  const reset = () => {
    setAdjustment("");
    setResult(null);
  };

  return (
    <Popover open={open} onOpenChange={(o) => { setOpen(o); if (!o) reset(); }}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid={testId}
          title="Mejorar con IA — escribe qué quieres cambiar"
          aria-label="Refinar con IA"
          className="shrink-0 p-2 text-[#8A8F98] hover:text-[#D95D39] transition-colors relative"
        >
          <Wand2 className="w-4 h-4" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        side="left"
        className="w-96 rounded-sm border-[#E2DCD0] p-3 shadow-lg"
        data-testid={`${testId}-popover`}
      >
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label className="text-[10px] uppercase tracking-widest text-[#8A8F98]">
              Mejorar con IA
            </Label>
            {result && (
              <button onClick={reset} className="text-[10px] text-[#8A8F98] hover:text-[#0A1128]">
                Nueva petición
              </button>
            )}
          </div>

          <p className="text-[10px] text-[#8A8F98] leading-relaxed">
            ¿Cómo quieres ajustar este {fieldLabel(field)}? La IA usará el resto del email como contexto y respetará tu voz de marca.
          </p>

          <Textarea
            data-testid={`${testId}-input`}
            placeholder="Ej: más urgente · menciona Marrakech · más corto · más sensorial · menos comercial"
            value={adjustment}
            onChange={e => setAdjustment(e.target.value)}
            rows={3}
            className="rounded-sm text-sm"
            disabled={loading}
          />

          <Button
            onClick={run}
            disabled={loading || !adjustment.trim()}
            className="w-full rounded-sm bg-[#D95D39] hover:bg-[#c54e2e] text-white h-8 text-xs"
            data-testid={`${testId}-run`}
          >
            {loading
              ? <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> Refinando…</>
              : <><Sparkles className="w-3.5 h-3.5 mr-1.5" /> Generar variación</>}
          </Button>

          {result && (
            <div className="space-y-2 pt-2 border-t border-[#E2DCD0]" data-testid={`${testId}-result`}>
              <div>
                <Label className="text-[10px] uppercase text-[#8A8F98]">Propuesta</Label>
                <div className="mt-1 p-2 border border-[#D95D39]/50 bg-[#D95D39]/5 rounded-sm text-sm text-[#0A1128] whitespace-pre-wrap">
                  {result.suggestion}
                </div>
                <div className="mt-1.5 flex gap-1.5">
                  <Button
                    size="sm"
                    onClick={() => accept(result.suggestion)}
                    className="rounded-sm bg-[#0A1128] hover:bg-[#1a2240] text-white h-7 text-xs flex-1"
                    data-testid={`${testId}-accept`}
                  >
                    <Check className="w-3 h-3 mr-1" /> Usar esta
                  </Button>
                </div>
              </div>

              {result.alternatives?.length > 0 && (
                <div>
                  <Label className="text-[10px] uppercase text-[#8A8F98]">Alternativas (click para usar)</Label>
                  <div className="flex flex-col gap-1 mt-1">
                    {result.alternatives.map((alt, i) => (
                      <button
                        key={i}
                        data-testid={`${testId}-alt-${i}`}
                        onClick={() => accept(alt)}
                        className="text-left text-xs p-2 border border-[#E2DCD0] rounded-sm hover:border-[#D95D39] hover:bg-[#D95D39]/5 transition-colors whitespace-pre-wrap"
                      >
                        {alt}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}

function fieldLabel(field) {
  return {
    subject_line:    "asunto",
    preheader:       "preheader",
    header_text:     "cabecera",
    intro_paragraph: "intro",
    module_heading:  "título del módulo",
    module_body:     "texto del módulo",
  }[field] || "campo";
}

// =============================================================================
// Shutterstock search dialog — manual download (opens shutterstock.com)
// =============================================================================

const SS_PHOTO_BASE = "https://www.shutterstock.com/image-photo/-";

function ShutterstockSearchDialog({ open, onOpenChange, defaultQuery, moduleIndex, onPick }) {
  // Initial state derived directly from props — no useEffect needed because
  // the parent re-mounts this component via `key` whenever defaultQuery changes.
  const [query, setQuery] = useState(defaultQuery || "");
  const [orientation, setOrientation] = useState("horizontal");
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState([]);
  const [total, setTotal] = useState(0);
  const [didAutoSearch, setDidAutoSearch] = useState(false);

  const runSearch = async (forceTerm) => {
    const candidate = typeof forceTerm === "string" ? forceTerm : query;
    const term = (candidate || "").trim();
    if (!term) return;
    setLoading(true);
    try {
      const r = await api.get("/email-designer/shutterstock/search", {
        params: { query: term, per_page: 24, orientation },
      });
      setResults(r.data.items || []);
      setTotal(r.data.total || 0);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error buscando en Shutterstock");
    } finally {
      setLoading(false);
    }
  };

  // Auto-run search the first time the dialog opens with a default query.
  if (open && !didAutoSearch && (defaultQuery || "").trim()) {
    setDidAutoSearch(true);
    runSearch(defaultQuery);
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto rounded-sm">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">
            Buscar fotos en Shutterstock
            {moduleIndex !== null && <span className="text-sm text-[#8A8F98] ml-2">· Módulo {moduleIndex + 1}</span>}
          </DialogTitle>
          <DialogDescription className="text-xs text-[#8A8F98]">
            Click en una foto abre Shutterstock.com en una pestaña nueva. Descárgala con tu cuenta y súbela tú a Mailchimp.
          </DialogDescription>
        </DialogHeader>

        <div className="flex gap-2 my-3">
          <Input
            data-testid="ss-search-query"
            placeholder="Ej: Andalusia spring courtyard"
            value={query}
            onChange={e => setQuery(e.target.value)}
            onKeyDown={e => e.key === "Enter" && runSearch()}
            className="rounded-sm"
          />
          <Select value={orientation} onValueChange={setOrientation}>
            <SelectTrigger className="w-40 rounded-sm"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="horizontal">Horizontal</SelectItem>
              <SelectItem value="vertical">Vertical</SelectItem>
              <SelectItem value="square">Cuadrado</SelectItem>
            </SelectContent>
          </Select>
          <Button onClick={runSearch} disabled={loading} className="rounded-sm bg-[#D95D39] hover:bg-[#c54e2e] text-white" data-testid="ss-search-btn">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
          </Button>
        </div>

        {total > 0 && (
          <p className="text-xs text-[#8A8F98]">{total.toLocaleString()} resultados · Click en una foto = se abre Shutterstock para descargar + se fija como elegida en este módulo</p>
        )}

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 mt-3">
          {results.map(item => (
            <a
              key={item.id}
              href={SS_PHOTO_BASE + item.id}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`ss-photo-${item.id}`}
              onClick={() => onPick && onPick(item)}
              className="group relative border border-[#E2DCD0] rounded-sm overflow-hidden text-left block"
              title={item.description}
            >
              <img src={item.preview_url} alt={item.description}
                   className="w-full h-36 object-cover" loading="lazy" />
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/50 transition-colors flex items-center justify-center">
                <span className="opacity-0 group-hover:opacity-100 text-white text-xs font-medium px-3 py-1.5 bg-[#D95D39] rounded-sm flex items-center gap-1">
                  Elegir + abrir Shutterstock <ExternalLink className="w-3 h-3" />
                </span>
              </div>
              <div className="absolute bottom-1 left-1 right-1 text-[10px] text-white truncate font-medium opacity-0 group-hover:opacity-100 transition-opacity drop-shadow-md">
                ID {item.id}
              </div>
            </a>
          ))}
        </div>

        {results.length === 0 && !loading && (
          <p className="text-center text-sm text-[#8A8F98] py-12">
            {query ? "Sin resultados. Prueba otras palabras." : "Escribe una búsqueda en inglés y pulsa enter."}
          </p>
        )}
      </DialogContent>
    </Dialog>
  );
}


// =============================================================================
// History Dialog — saved AI generations (one per generate, auto-saved on edits)
// =============================================================================

function HistoryDialog({ open, onOpenChange, drafts, loading, currentId, onLoad, onDelete, onRefresh }) {
  const fmt = (iso) => {
    if (!iso) return "";
    try {
      const d = new Date(iso);
      return d.toLocaleDateString("es-ES", { day: "numeric", month: "short" }) + " · " +
             d.toLocaleTimeString("es-ES", { hour: "2-digit", minute: "2-digit" });
    } catch { return iso; }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-hidden flex flex-col rounded-sm">
        <DialogHeader>
          <div className="flex items-center justify-between gap-2">
            <div>
              <DialogTitle className="font-display text-xl">Mis generaciones</DialogTitle>
              <DialogDescription className="text-xs text-[#8A8F98]">
                Cada vez que generas textos, se guardan aquí. Edita inline y se autoguarda. Click en una para retomarla.
              </DialogDescription>
            </div>
            <Button
              size="sm"
              variant="outline"
              onClick={onRefresh}
              disabled={loading}
              className="rounded-sm h-8"
              data-testid="history-refresh-btn"
            >
              {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
            </Button>
          </div>
        </DialogHeader>

        <div className="overflow-y-auto -mx-6 px-6 divide-y divide-[#E2DCD0]">
          {loading && drafts.length === 0 && (
            <div className="py-12 text-center text-sm text-[#8A8F98]">Cargando…</div>
          )}
          {!loading && drafts.length === 0 && (
            <div className="py-16 text-center" data-testid="history-empty">
              <History className="w-10 h-10 mx-auto text-[#E2DCD0] mb-2" />
              <p className="text-sm text-[#8A8F98]">Aún no tienes borradores guardados.</p>
              <p className="text-xs text-[#8A8F98] mt-1">Genera tu primer email y aparecerá aquí automáticamente.</p>
            </div>
          )}
          {drafts.map(d => {
            const isCurrent = d.id === currentId;
            return (
              <div
                key={d.id}
                data-testid={`history-item-${d.id}`}
                className={`py-3 flex items-start justify-between gap-3 ${isCurrent ? "bg-[#F9F8F6]/60 -mx-3 px-3 rounded-sm" : ""}`}
              >
                <button
                  className="flex-1 text-left min-w-0"
                  onClick={() => onLoad(d.id)}
                  data-testid={`history-load-${d.id}`}
                >
                  <div className="flex items-center gap-2 mb-1">
                    <p className="font-medium text-sm text-[#0A1128] truncate">
                      {d.title || d.subject_line || "(sin asunto)"}
                    </p>
                    {isCurrent && (
                      <Badge variant="outline" className="rounded-sm text-[9px] border-[#D95D39] text-[#D95D39] px-1.5 py-0">
                        ACTUAL
                      </Badge>
                    )}
                  </div>
                  <div className="flex items-center gap-2 text-[10px] text-[#8A8F98]">
                    <span>{fmt(d.updated_at || d.created_at)}</span>
                    <span>·</span>
                    <span>{d.module_count} módulos</span>
                    {d.mode && (<><span>·</span><span>{d.mode === "brief" ? "Brief completo" : "Por módulo"}</span></>)}
                    {d.email_type && (<><span>·</span><span>{d.email_type}</span></>)}
                  </div>
                </button>
                <button
                  onClick={(e) => { e.stopPropagation(); onDelete(d.id); }}
                  className="shrink-0 p-2 text-[#8A8F98] hover:text-red-600 transition-colors"
                  title="Eliminar"
                  data-testid={`history-delete-${d.id}`}
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            );
          })}
        </div>
      </DialogContent>
    </Dialog>
  );
}
